import React, { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { arEG, enUS, es, fr } from "date-fns/locale";
import { Calendar } from "../UIElements/Calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../UIElements/Popover";
import { Icon } from "../UIElements/Icon";
import { useUiLocale } from "../../../hooks/useUiLocale";

const CALENDAR_LOCALES = { ar: arEG, en: enUS, es, fr };

const PromotionTimeline = ({ start, end, onChangeStart, onChangeEnd }) => {
  const { t } = useTranslation(["discounts", "common"]);
  const [isStartOpen, setIsStartOpen] = useState(false);
  const [isEndOpen, setIsEndOpen] = useState(false);
  const { locale } = useUiLocale();
  const calendarLocale = CALENDAR_LOCALES[locale];
  const calendarLabels = {
    labelNext: () => t("common:calendar.nextMonth"),
    labelPrevious: () => t("common:calendar.previousMonth"),
    labelMonthDropdown: () => t("common:calendar.chooseMonth"),
    labelYearDropdown: () => t("common:calendar.chooseYear"),
    labelNav: () => t("common:calendar.navigation"),
    labelDayButton: (date, modifiers) =>
      [
        modifiers.today ? t("common:date.today") : null,
        format(date, "PPPP", { locale: calendarLocale }),
        modifiers.selected ? t("common:calendar.selected") : null,
      ]
        .filter(Boolean)
        .join(", "),
  };
  const id = useId();
  const startTriggerId = `promotion-start-${id}`;
  const endTriggerId = `promotion-end-${id}`;

  const startDate = useMemo(() => {
    if (!start) {
      return undefined;
    }
    const parsed = new Date(`${start}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return undefined;
    }
    return parsed;
  }, [start]);

  const endDate = useMemo(() => {
    if (!end) {
      return undefined;
    }
    const parsed = new Date(`${end}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return undefined;
    }
    return parsed;
  }, [end]);

  const handleSelectStartDate = (date) => {
    if (!date) {
      return;
    }
    onChangeStart(format(date, "yyyy-MM-dd"));
    setIsStartOpen(false);
  };

  const handleSelectEndDate = (date) => {
    if (!date) {
      return;
    }
    onChangeEnd(format(date, "yyyy-MM-dd"));
    setIsEndOpen(false);
  };

  return (
    <div className="ordering-discounts-panel border-border bg-background rounded-xl border p-6">
      <h3 className="text-muted-foreground mb-4 text-sm font-semibold tracking-wider uppercase">
        {t("timeline.title")}
      </h3>
      <div className="space-y-4">
        <div>
          <label
            htmlFor={startTriggerId}
            className="text-muted-foreground mb-1 block text-[length:var(--font-size-product-ui)]"
          >
            {t("timeline.startDate")}
          </label>
          <Popover open={isStartOpen} onOpenChange={setIsStartOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                id={startTriggerId}
                className="ordering-discounts-timeline-trigger border-border bg-background focus:ring-ring text-foreground relative w-full cursor-pointer rounded-lg border py-2 pr-3 pl-10 text-left text-[length:var(--font-size-product-ui)] outline-none focus:ring-1"
                aria-label={t("timeline.selectStartDate")}
              >
                <span className="block truncate">
                  {start || t("timeline.selectDate")}
                </span>
                <Icon
                  name="Calendar"
                  size={14}
                  className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="border-border bg-background w-auto border p-0"
            >
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={handleSelectStartDate}
                defaultMonth={startDate}
                initialFocus
                locale={calendarLocale}
                dir={locale === "ar" ? "rtl" : "ltr"}
                labels={calendarLabels}
                formatters={{
                  formatMonthDropdown: (date) =>
                    format(date, "MMM", { locale: calendarLocale }),
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <label
            htmlFor={endTriggerId}
            className="text-muted-foreground mb-1 block text-[length:var(--font-size-product-ui)]"
          >
            {t("timeline.endDate")}
          </label>
          <Popover open={isEndOpen} onOpenChange={setIsEndOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                id={endTriggerId}
                className="ordering-discounts-timeline-trigger border-border bg-background focus:ring-ring text-foreground relative w-full cursor-pointer rounded-lg border py-2 pr-3 pl-10 text-left text-[length:var(--font-size-product-ui)] outline-none focus:ring-1"
                aria-label={t("timeline.selectEndDate")}
              >
                <span className="block truncate">
                  {end || t("timeline.selectDate")}
                </span>
                <Icon
                  name="Calendar"
                  size={14}
                  className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="border-border bg-background w-auto border p-0"
            >
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={handleSelectEndDate}
                defaultMonth={endDate}
                initialFocus
                locale={calendarLocale}
                dir={locale === "ar" ? "rtl" : "ltr"}
                labels={calendarLabels}
                formatters={{
                  formatMonthDropdown: (date) =>
                    format(date, "MMM", { locale: calendarLocale }),
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default PromotionTimeline;
